import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest, setAdminGradeOverride as setGlobalGradeOverride } from '@/lib/queryClient';
import { identifyUser, trackEvent, resetUser } from '@/lib/mixpanel';
import { identifyPostHogUser, capturePostHogEvent, resetPostHogUser } from '@/lib/posthog';

interface User {
  id: string;
  username?: string | null;
  firstName: string;
  lastName: string;
  email: string;
  grade: number;
  school?: string;
  role: string;
  createdAt: string;
  lastActiveAt: string;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  subscriptionStatus?: string;
  subscriptionPlan?: string;
  subscriptionEndDate?: string;
  subscribedGrade?: number;
  subscriptionCancelAtPeriodEnd?: boolean;
  forcePasswordReset?: boolean;
  deletionRequestedAt?: string | null;
  parentFirstName?: string | null;
  parentLastName?: string | null;
  parentEmail?: string | null;
  parentPhone?: string | null;
  xp?: number;
  level?: number;
  profilePhotoUrl?: string | null;
  profilePhotoBonusAwarded?: boolean;
  uiPreferences?: Record<string, any> | null;
  gradeConfirmedAt?: string | null;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (identifier: string, password: string) => Promise<void>;
  register: (userData: RegisterData) => Promise<any>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
  adminGradeOverride: number | null;
  setAdminGradeOverride: (grade: number | null) => void;
}

interface RegisterData {
  password: string;
  firstName: string;
  lastName: string;
  email: string;
  grade: number;
  school?: string;
  parentPhone: string;
  signupSource?: string;
  signupUtmSource?: string;
  signupUtmMedium?: string;
  signupUtmCampaign?: string;
  marketingOptIn?: boolean;
  voucherCode?: string;
}

const AuthContext = createContext<AuthContextType | null>(null);

function buildPostHogTraits(u: User) {
  return {
    $name: `${u.firstName} ${u.lastName}`,
    $email: u.email,
    username: u.username,
    grade: u.grade,
    school: u.school,
    role: u.role,
    created_at: u.createdAt,
    subscription_status: u.subscriptionStatus,
    subscription_plan: u.subscriptionPlan,
    subscription_end_date: u.subscriptionEndDate,
    subscribed_grade: u.subscribedGrade,
    parent_name: u.parentFirstName && u.parentLastName
      ? `${u.parentFirstName} ${u.parentLastName}`
      : u.parentFirstName || null,
    parent_email: u.parentEmail,
    parent_phone: u.parentPhone,
    xp: u.xp,
    level: u.level,
  };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminGradeOverride, setAdminGradeOverrideState] = useState<number | null>(() => {
    try {
      const stored = localStorage.getItem('adminGradeOverride');
      if (stored) {
        const grade = parseInt(stored);
        setGlobalGradeOverride(grade);
        return grade;
      }
    } catch {}
    return null;
  });
  const queryClient = useQueryClient();

  const setAdminGradeOverride = useCallback((grade: number | null) => {
    setAdminGradeOverrideState(grade);
    setGlobalGradeOverride(grade);
    if (grade) {
      localStorage.setItem('adminGradeOverride', String(grade));
    } else {
      localStorage.removeItem('adminGradeOverride');
    }
    // Refetch everything with new grade
    queryClient.invalidateQueries();
  }, [queryClient]);

  // Get current user
  const { data: user, isLoading } = useQuery<{ user: User }>({
    queryKey: ['/api/auth/me'],
    retry: false,
    refetchOnWindowFocus: false,
    // Only run if we think we might be authenticated
    enabled: isAuthenticated || (!isAuthenticated && typeof window !== 'undefined'),
  });

  // Update auth status based on user data
  useEffect(() => {
    if (user && 'user' in user && user.user) {
      setIsAuthenticated(true);
      
      // Identify user in Mixpanel
      identifyUser(user.user.id, {
        username: user.user.username,
        firstName: user.user.firstName,
        lastName: user.user.lastName,
        grade: user.user.grade,
        school: user.user.school,
        role: user.user.role,
        subscriptionStatus: user.user.subscriptionStatus,
        subscriptionPlan: user.user.subscriptionPlan,
      });
      // Identify user in PostHog
      identifyPostHogUser(user.user.id, buildPostHogTraits(user.user));
    } else if (user === null) {
      setIsAuthenticated(false);
    }
  }, [user]);

  // Login mutation
  const loginMutation = useMutation({
    // `identifier` is an email OR a username: the leaderboard and profile call students
    // by their username, so that is what a lot of them type here.
    mutationFn: async ({ identifier, password }: { identifier: string; password: string }) => {
      const response = await apiRequest('POST', '/api/auth/login', { identifier, password });
      return response.json();
    },
    onSuccess: (data) => {
      setIsAuthenticated(true);
      queryClient.setQueryData(['/api/auth/me'], data);
      queryClient.invalidateQueries({ queryKey: ['/api/auth/me'] });
      
      // Track login event
      trackEvent('User Login', {
        username: data.user.username,
        grade: data.user.grade,
        role: data.user.role,
      });
      identifyPostHogUser(data.user.id, buildPostHogTraits(data.user));
      capturePostHogEvent('User Login', {
        grade: data.user.grade,
        role: data.user.role,
      });
    },
  });

  // Register mutation
  const registerMutation = useMutation({
    mutationFn: async (userData: RegisterData) => {
      const response = await apiRequest('POST', '/api/auth/register', userData);
      return response.json();
    },
    onSuccess: (data) => {
      setIsAuthenticated(true);
      queryClient.setQueryData(['/api/auth/me'], data);
      queryClient.invalidateQueries({ queryKey: ['/api/auth/me'] });
      
      // Track registration event
      trackEvent('User Registration', {
        username: data.user.username,
        grade: data.user.grade,
        school: data.user.school,
      });
      identifyPostHogUser(data.user.id, buildPostHogTraits(data.user));
      capturePostHogEvent('User Signed Up', {
        grade: data.user.grade,
      });
    },
  });

  // Logout mutation
  const logoutMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest('POST', '/api/auth/logout');
      return response.json();
    },
    onSuccess: () => {
      // Track logout event BEFORE reset (while user is still identified)
      trackEvent('User Logout');
      capturePostHogEvent('User Logout');

      setIsAuthenticated(false);
      queryClient.setQueryData(['/api/auth/me'], null);
      queryClient.clear();

      // Reset Mixpanel user identity
      resetUser();
      // Reset PostHog user identity
      resetPostHogUser();
    },
  });

  const rawUser = (user && 'user' in user ? user.user : null) as User | null;
  // Override grade for admins when grade switcher is active
  const effectiveUser = rawUser && rawUser.role === 'admin' && adminGradeOverride
    ? { ...rawUser, grade: adminGradeOverride }
    : rawUser;

  const contextValue: AuthContextType = {
    user: effectiveUser,
    isLoading: isLoading || loginMutation.isPending || registerMutation.isPending,
    login: async (identifier: string, password: string) => {
      await loginMutation.mutateAsync({ identifier, password });
    },
    register: async (userData: RegisterData) => {
      return await registerMutation.mutateAsync(userData);
    },
    logout: async () => {
      setAdminGradeOverride(null);
      await logoutMutation.mutateAsync();
    },
    isAuthenticated,
    adminGradeOverride,
    setAdminGradeOverride,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}